import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { generationsApi, printApi, purchaseApi } from "@/lib/api/endpoints";
import { download } from "@/lib/api/client";
import { Spinner } from "@/components/ui";
import { formatDateTime } from "@/lib/utils/format";
import type { SharePayload } from "@/lib/types/api";
import type { PurchaseAction } from "@/lib/types/config";
import { PaymentSheet } from "./components/PaymentSheet";
import { NotFoundPage } from "./NotFoundPage";

/**
 * The guest's own photo — and the checkout.
 *
 * ## Why payment lives here and not on the kiosk
 *
 * The booth is a queue. A guest standing at the kiosk typing a phone number,
 * waiting for a USSD push, and approving it in telebirr is thirty to sixty
 * seconds during which nobody else can use the booth. At a busy event that is
 * the whole bottleneck.
 *
 * So the kiosk hands off: it shows a QR, the guest scans it, and everything
 * that takes time happens on their own phone while the next guest is already
 * being photographed. Throughput stops depending on how fast people pay.
 *
 * It's also just better for the guest — they pay on their own device, at their
 * own pace, and walk away with the photo in their camera roll rather than a
 * promise.
 *
 * ## What's gated
 *
 * The page always loads. Gating the page would show an error to someone who
 * scanned a code specifically to resolve it. What's gated is the *file*:
 * `preview_url` is a downscaled copy, and `image_url` is null until they've
 * paid. That matters more here than on the kiosk — a phone can long-press to
 * save any image on screen, so showing the full photo before payment is
 * indistinguishable from giving it away.
 */
export function SharePage() {
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const paymentResult = searchParams.get("payment");
  const paymentAction = searchParams.get("action") as PurchaseAction | null;

  const [data, setData] = useState<SharePayload | null>(null);
  const [failed, setFailed] = useState(false);
  const [paying, setPaying] = useState<PurchaseAction | null>(null);
  const [busy, setBusy] = useState<PurchaseAction | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await generationsApi.share(token));
    } catch {
      setFailed(true);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  if (failed) {
    return (
      <NotFoundPage
        title="Photo not found"
        message="This link may have expired, or the photo is still being created. Try scanning again in a moment."
      />
    );
  }

  if (!data) {
    return (
      <div className="grid h-full place-items-center bg-ink-850">
        <Spinner size={28} className="text-accent" />
      </div>
    );
  }

  const granted = (action: PurchaseAction) => data.actions[action]?.granted === true;
  const payable = (action: PurchaseAction) => data.actions[action]?.payable === true;
  const priceOf = (action: PurchaseAction) =>
    `${((data.actions[action]?.cents ?? 0) / 100).toFixed(2)} ${data.currency}`;

  /** Runs the action itself, once it's been paid for (or was free all along). */
  async function perform(action: PurchaseAction) {
    setBusy(action);
    setNotice(null);

    try {
      // Re-fetch first: `image_url` was null while locked, so the fresh payload
      // is where the real file arrives.
      const fresh = await generationsApi.share(token);
      setData(fresh);

      if (action === "print") {
        await printApi.create(fresh.event_slug, { public_token: token });
        setNotice("Sent to the printer at the booth — collect it there.");
        return;
      }

      if (action === "share") {
        const url = window.location.href;
        if (navigator.share) {
          await navigator.share({ title: fresh.event_name, url }).catch(() => undefined);
        } else {
          await navigator.clipboard.writeText(url).catch(() => undefined);
          setNotice("Link copied.");
        }
        return;
      }

      // Download. Mobile browsers are inconsistent about programmatic downloads,
      // so this tries, and the Save button below stays as the reliable path
      // rather than assuming it worked.
      if (fresh.actions.download?.granted) {
        await download(
          purchaseApi.downloadUrl(fresh.event_slug, token),
          `${fresh.event_name}-photo.jpg`,
          { anonymous: true },
        );
        setNotice("Saved. If nothing happened, use the button below.");
      }
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "That didn't work — please try again.");
    } finally {
      setBusy(null);
    }
  }

  function onTap(action: PurchaseAction) {
    if (granted(action) || !payable(action)) return void perform(action);
    setPaying(action);
  }

  const unlocked = data.actions.download?.granted === true;

  return (
    <div className="flex min-h-full flex-col items-center gap-5 bg-ink-850 px-5 py-8">
      <header className="text-center">
        <h1 className="text-lg font-semibold">{data.event_name}</h1>
        <p className="text-xs text-haze-400">{formatDateTime(data.created_at)}</p>
      </header>

      {paymentResult === "failed" && (
        <div className="w-full max-w-xs rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-center text-sm text-danger">
          We couldn’t verify your payment. You can try again below.
        </div>
      )}

      {paymentResult === "paid" && paymentAction === "download" && granted("download") && (
        <div className="flex items-center gap-2 rounded-full border border-ok/40 bg-ok/10 px-4 py-2 text-xs font-semibold text-ok">
          <span className="animate-pulse">✓</span> Recently paid — download unlocked
        </div>
      )}

      <div className="relative">
        <img
          src={data.image_url ?? data.preview_url}
          alt="Your photo"
          className="max-h-[52vh] w-auto rounded-2xl shadow-2xl"
        />
        {!unlocked && (
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/70 px-3 py-1 text-2xs uppercase tracking-wider text-white/90">
            Preview — unlock for the full photo
          </span>
        )}
      </div>

      <div className="flex w-full max-w-xs flex-col gap-2.5">
        {(["download", "print", "share"] as PurchaseAction[]).map((action) => {
          const isGranted = granted(action);
          const showPrice = !isGranted && payable(action);

          return (
            <button
              key={action}
              onClick={() => onTap(action)}
              disabled={busy !== null}
              className="flex h-14 items-center justify-between rounded-xl bg-accent px-5 text-accent-ink disabled:opacity-50"
            >
              <span className="text-sm font-bold uppercase tracking-wide">
                {busy === action ? "…" : ACTION_LABELS[action]}
              </span>
              <span className="text-sm font-semibold tabular-nums">
                {showPrice ? priceOf(action) : isGranted ? "Ready" : "Free"}
              </span>
            </button>
          );
        })}
      </div>

      {notice && (
        <p className="max-w-xs text-center text-xs leading-relaxed text-haze-300">{notice}</p>
      )}

      <p className="max-w-xs text-center text-xs leading-relaxed text-haze-400">
        {unlocked
          ? "Tap Download to save the full photo to your device."
          : "Pay on your own phone. The booth is free for the next person."}
      </p>

      {paying && (
        <PaymentSheet
          slug={data.event_slug}
          action={paying}
          amountCents={data.actions[paying]?.cents ?? 0}
          currency={data.currency}
          identify={{ public_token: token }}
          testMode={data.test_mode}
          paymentProvider={data.payment_provider}
          paymentMode={data.payment_mode ?? "web_h5"}
          fullScreen
          onPaid={() => { setPaying(null); void perform(paying); }}
          onClose={() => setPaying(null)}
        />
      )}
    </div>
  );
}

const ACTION_LABELS: Record<PurchaseAction, string> = {
  download: "Download",
  print: "Print at booth",
  share: "Share link",
};
